/**
 * User Settings: somebody's own choices, in the same shape as Feature
 * Enablements — tabs, a record to search for, a table, one Save.
 *
 * Two differences, both from the data rather than from taste:
 *
 * - there is no Source column. A setting has no group record to inherit
 *   from, so a row is either set on this person or still the default;
 * - the Value column is not a toggle. A setting's `valueType` says what it
 *   is — a theme, a language, kilometres, a list of weekdays — so the editor
 *   is chosen per row.
 */

import type { UserSettingListItem, UserSettingsAudience } from "@wallyo/contracts/console/configuration";
import type { SettingValue } from "@wallyo/contracts/settings";
import { useMemo, useState } from "react";

import { listUserSettings, saveUserSettings } from "../../api/console-api";
import { ConsoleApiError } from "../../api/errors";
import { useApi } from "../../api/context";
import { categoryName, settingName, t } from "../../i18n";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { describeConfigFailure } from "../config-refusal";
import { cleanUuid, isCompleteUuid } from "../features/feature-save-model";
import { countLabel, SaveBar } from "../features/FeatureTableView";
import { WriteFailureNotice, useIntentWrite } from "../team/TeamDialogs";
import { SettingValueEditor } from "./SettingValueEditor";
import {
  EMPTY_SETTINGS_DRAFT,
  changedSettings,
  isSettingChanged,
  saveSettingsRequest,
  settingsSaveProblem,
  shownSettingValue,
  withSettingValue,
  type SettingsDraft,
} from "./user-settings-model";

const describeFailure = (error: unknown) =>
  describeConfigFailure(error, { critical: "features.refused.critical" }, "features.stale");

/** A UUID that named nobody on this tab: unknown, or the other tab's. */
function missingOf(error: unknown, audience: UserSettingsAudience): "none" | "unknown" | "otherTab" {
  if (!(error instanceof ConsoleApiError)) return "none";
  const other = audience === "app" ? "uuid_is_staff" : "uuid_is_customer";
  if (error.reason === other) return "otherTab";
  return error.reason === "no_such_user" || error.reason === "no_such_staff" ? "unknown" : "none";
}

export function UserSettingsScreen() {
  const { client } = useApi();
  const [audience, setAudience] = useState<UserSettingsAudience>("app");
  const [typed, setTyped] = useState("");
  const [searched, setSearched] = useState<number | null>(null);
  const [searches, setSearches] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<SettingsDraft>(EMPTY_SETTINGS_DRAFT);
  const [reason, setReason] = useState("");
  const alerts = useAlerts();
  const save = useIntentWrite();

  const subject = useMemo(() => (searched === null ? null : { audience, uuid: searched }), [audience, searched]);
  const key = subject === null ? null : `${audience}:${subject.uuid}#${searches}`;
  const list = useLoad(subject === null ? null : () => listUserSettings(client, subject), key);

  const loading = subject !== null && list.state.status === "loading";
  const settings = list.state.data?.settings ?? [];
  const changed = changedSettings(settings, draft);
  const missing = subject !== null && list.state.status === "failed" ? missingOf(list.state.error, audience) : "none";

  const clear = () => {
    setDraft(EMPTY_SETTINGS_DRAFT);
    setReason("");
    setProblem(null);
  };

  const search = () => {
    if (!isCompleteUuid(typed)) {
      setProblem(t("features.subject.needsUuid"));
      return;
    }
    clear();
    setSearched(Number(cleanUuid(typed)));
    setSearches((count) => count + 1);
  };

  // Another catalogue is another table, and a UUID on one tab is rarely the
  // same person on the other, so the draft never crosses.
  const switchTo = (next: UserSettingsAudience) => {
    if (next === audience) return;
    setAudience(next);
    setSearched(null);
    clear();
  };

  const submit = () => {
    if (subject === null) return;
    save.edited();
    void save.run(
      settingsSaveProblem(reason, settings, draft),
      (idempotencyKey) =>
        saveUserSettings(client, saveSettingsRequest(subject, reason, settings, draft), { idempotencyKey }),
      ({ saved }) => {
        clear();
        alerts.ok(countLabel("done", saved));
        list.reload();
      },
    );
  };

  return (
    <>
      <PageTop title={t("settings.title")} />
      <div className="surface-tabs" role="tablist">
        {(["app", "employee"] as const).map((each) => (
          <button
            key={each}
            type="button"
            role="tab"
            aria-selected={audience === each}
            className={audience === each ? "surface-tab on" : "surface-tab"}
            onClick={() => switchTo(each)}
          >
            {t(`settings.tab.${each}` as const)}
          </button>
        ))}
      </div>

      <form
        className="card subject-bar"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
      >
        <label className="subject-uuid">
          <span className="subject-label">{t("features.subject.uuid")}</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={typed}
            placeholder={t("features.subject.uuidHint")}
            onChange={(event) => {
              setProblem(null);
              setTyped(cleanUuid(event.target.value));
            }}
          />
        </label>
        <button type="submit" className="btn primary" disabled={loading}>
          {loading ? t("features.subject.searching") : t("features.subject.search")}
        </button>
        {problem ? <span className="subject-problem">{problem}</span> : null}
        <span className="subject-who">
          {missing !== "none" ? (
            <span className="subject-pill missing" role="status">
              <span className="subject-name">
                {t(missing === "otherTab" ? "features.subject.isStaff" : "features.subject.unknown")}
              </span>
            </span>
          ) : list.state.data ? (
            <span className="subject-pill">
              <span className="subject-name">{list.state.data.subject.name}</span>
            </span>
          ) : null}
        </span>
      </form>

      {subject === null ? (
        <div className="card">
          <div className="empty">{t("features.subject.pickFirst")}</div>
        </div>
      ) : null}
      {subject !== null && list.state.status === "failed" && missing === "none" ? (
        <LoadFailed
          message={t("features.loadFailed")}
          retry={t("features.retry")}
          error={list.state.error}
          onRetry={list.reload}
        />
      ) : null}
      {!list.state.data && loading ? <Loading text={t("features.loading")} /> : null}

      {list.state.data && missing === "none" ? (
        <>
          <div className="card">
            <table className="config-list feature-table">
              <thead>
                <tr>
                  <th>{t("settings.col.setting")}</th>
                  <th>{t("features.col.category")}</th>
                  <th>{t("features.col.value")}</th>
                </tr>
              </thead>
              <tbody>
                {settings.map((setting) => (
                  <SettingRow
                    key={setting.settingId}
                    setting={setting}
                    draft={draft}
                    onChange={(value) => {
                      save.edited();
                      setDraft((current) => withSettingValue(current, setting, value));
                    }}
                  />
                ))}
              </tbody>
            </table>
            {settings.length === 0 ? <div className="empty">{t("settings.none")}</div> : null}
          </div>
          {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} onReload={list.reload} /> : null}
          <SaveBar
            count={changed.length}
            reason={reason}
            pending={save.pending}
            problem={save.problem}
            onReason={(next) => {
              save.edited();
              setReason(next);
            }}
            onDiscard={clear}
            onSave={submit}
          />
        </>
      ) : null}
    </>
  );
}

function SettingRow(props: {
  setting: UserSettingListItem;
  draft: SettingsDraft;
  onChange: (value: SettingValue | null) => void;
}) {
  const { setting, draft, onChange } = props;
  const changed = isSettingChanged(setting, draft);
  const cleared = draft[setting.settingId] === null;
  return (
    <tr className={changed ? "dirty" : undefined}>
      <td>
        {settingName(setting.settingId)}
        {setting.setHere && !changed ? <span className="pill own">{t("settings.setHere")}</span> : null}
      </td>
      <td>{categoryName(setting.category)}</td>
      <td>
        <span className="value-cell">
          <SettingValueEditor
            setting={setting}
            value={shownSettingValue(setting, draft)}
            disabled={!setting.editable}
            onChange={onChange}
          />
          {/* Only a value set on this person can be taken away. */}
          {setting.setHere && setting.editable && !cleared ? (
            <button type="button" className="btn quiet small" onClick={() => onChange(null)}>
              {t("settings.clear")}
            </button>
          ) : null}
          {changed && !cleared ? <span className="value-note unsaved">{t("features.unsaved")}</span> : null}
          {cleared ? <span className="value-note cleared">{t("settings.default")}</span> : null}
          {!setting.editable ? <span className="value-note locked">{t("features.locked.grant")}</span> : null}
        </span>
      </td>
    </tr>
  );
}
